/**
 * PackagingSelector — Oro Grande
 *
 * Horizon dispatcha cart:update con questa struttura:
 *   event.detail.sourceId  → variant ID aggiunto (stringa)
 *   event.detail.data.productId → product ID
 *   event.detail.data.source  → 'product-form-component'
 *   event.detail.resource  → carrello completo (da /cart.js)
 */

const CART_UPDATE_EVENT = 'cart:update';
const PACKAGING_PROP = '_packaging_for';

class PackagingSelector extends HTMLElement {
  /** null = confezione inclusa (gratis), stringa = variant ID premium selezionato */
  #selectedVariantId = null;

  #isBusy = false;

  /** @type {HTMLDialogElement|null} */
  #previewDialog = null;

  connectedCallback() {
    this.#setupRadioListeners();
    this.#setupImagePreview();
    document.addEventListener(CART_UPDATE_EVENT, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(CART_UPDATE_EVENT, this.#onCartUpdate);
    this.#previewDialog?.remove();
  }

  get productId() {
    return this.dataset.productId;
  }

  // ── Selezione UI ─────────────────────────────────────────────────────────

  #setupRadioListeners() {
    this.querySelectorAll('.packaging-selector__radio').forEach((radio) => {
      radio.addEventListener('change', (e) => {
        const value = /** @type {HTMLInputElement} */ (e.target).value;
        this.#selectedVariantId = value === 'included' ? null : value;
        this.#updateSelectedState(value);
      });
    });
  }

  #updateSelectedState(selectedValue) {
    this.querySelectorAll('.packaging-selector__item').forEach((item) => {
      item.classList.toggle('is-selected', item.dataset.variantId === selectedValue);
    });
  }

  // ── Lightbox immagine ────────────────────────────────────────────────────

  #setupImagePreview() {
    const dialog = document.createElement('dialog');
    dialog.className = 'pkg-preview-dialog';
    dialog.innerHTML = `
      <button class="pkg-preview-dialog__close" type="button" aria-label="Chiudi">✕</button>
      <img class="pkg-preview-dialog__img" src="" alt="">
    `;
    document.body.appendChild(dialog);
    this.#previewDialog = dialog;

    dialog.querySelector('.pkg-preview-dialog__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });

    this.querySelectorAll('.packaging-selector__item[data-full-img]').forEach((item) => {
      const thumb = item.querySelector('.packaging-selector__thumb');
      if (!thumb) return;

      thumb.style.cursor = 'zoom-in';

      const openPreview = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const img = dialog.querySelector('img');
        img.src = item.dataset.fullImg;
        img.alt = item.querySelector('.packaging-selector__name')?.textContent || '';
        dialog.showModal();
      };

      thumb.addEventListener('click', openPreview);

      const zoomHint = thumb.querySelector('.packaging-selector__zoom-hint');
      if (zoomHint) zoomHint.addEventListener('click', openPreview);
    });
  }

  // ── Listener cart:update ─────────────────────────────────────────────────

  #onCartUpdate = async (event) => {
    if (this.#isBusy) return;

    // Horizon: sourceId = variant ID, data.productId = product ID, resource = carrello completo
    const { resource: cart, sourceId: addedVariantId, data } = event.detail ?? {};

    console.log('[pkg] cart:update ricevuto', {
      source: data?.source,
      addedVariantId,
      dataProductId: data?.productId,
      myProductId: this.productId,
      cartItems: cart?.items?.length,
      selectedVariantId: this.#selectedVariantId,
    });

    if (!cart?.items) return;

    this.#isBusy = true;
    try {
      let currentCart = cart;

      // Aggiunge packaging solo se è stato aggiunto il nostro prodotto
      const isOurProduct =
        data?.source === 'product-form-component' &&
        data?.productId?.toString() === this.productId;

      console.log('[pkg] isOurProduct:', isOurProduct, '| selectedVariantId:', this.#selectedVariantId);

      if (isOurProduct && addedVariantId) {
        const updated = await this.#maybeAddPackaging(currentCart, addedVariantId);
        if (updated) currentCart = updated;
      }

      // Rimuove confezioni orfane in tutti i casi (es. prodotto rimosso dal carrello)
      await this.#removeOrphanedPackaging(currentCart);
    } finally {
      this.#isBusy = false;
    }
  };

  // ── Aggiunge confezione premium ──────────────────────────────────────────

  async #maybeAddPackaging(cart, addedVariantId) {
    if (!this.#selectedVariantId) return null;

    const addedItem = cart.items.find(
      (item) => item.variant_id.toString() === addedVariantId.toString()
    );
    if (!addedItem) return null;

    // Evita duplicati se la stessa combinazione è già nel carrello
    const alreadyAdded = cart.items.some(
      (item) =>
        item.variant_id.toString() === this.#selectedVariantId &&
        item.properties?.[PACKAGING_PROP] === addedItem.key
    );
    if (alreadyAdded) return null;

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: parseInt(this.#selectedVariantId, 10),
            quantity: 1,
            properties: { [PACKAGING_PROP]: addedItem.key },
          },
        ],
      }),
    });

    if (!response.ok) {
      console.error('[packaging-selector] Errore aggiunta confezione:', response.status);
      return null;
    }

    const updatedCart = await fetch('/cart.js').then((r) => r.json());
    this.#dispatchCartRefresh(updatedCart);
    return updatedCart;
  }

  // ── Rimuove confezioni orfane ────────────────────────────────────────────

  async #removeOrphanedPackaging(cart) {
    const orphans = cart.items.filter((item) => {
      const parentKey = item.properties?.[PACKAGING_PROP];
      if (!parentKey) return false;
      return !cart.items.some((other) => other.key === parentKey);
    });

    if (orphans.length === 0) return;

    for (const orphan of orphans) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: orphan.key, quantity: 0 }),
      }).catch((err) => console.error('[packaging-selector] Errore rimozione orfano:', err));
    }

    const updatedCart = await fetch('/cart.js').then((r) => r.json());
    this.#dispatchCartRefresh(updatedCart);
  }

  // ── Aggiorna UI carrello ─────────────────────────────────────────────────

  #dispatchCartRefresh(cart) {
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'packaging-selector',
          data: {
            source: 'packaging-selector',
            itemCount: cart.item_count,
          },
        },
      })
    );
  }
}

if (!customElements.get('packaging-selector')) {
  customElements.define('packaging-selector', PackagingSelector);
}
